<template>
  <v-menu
    v-if="hasAnyItemsInMenu && asset"
    v-model="showingMenu"
    v-bind:target="[positionX, positionY]"
    :close-on-content-click="false"
    :persistent="false"
    absolute
    location="bottom"
  >
    <template v-slot:activator="{ props }">
      <slot v-bind="{ props }" name="activator" />
    </template>

    <v-list data-cy="podFiles-tree-actions-list">
      <v-list-item
        v-if="canShowViewAsset"
        @click="showViewAssetDialog()"
      >
        <template v-slot:prepend>
          <v-icon size="small">mdi-play-box-outline</v-icon>
        </template>
        <v-list-item-title>{{ $t('routes.pod.viewAsset') }}</v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="canShowDownloadAssetItem"
        @click="downloadFile(asset)"
        data-cy="podFiles-tree-actions-downloadAsset"
      >
        <template v-slot:prepend>
          <v-icon size="small">mdi-download-box-outline</v-icon>
        </template>
        <v-list-item-title>{{ $t('routes.pod.downloadAsset') }}</v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="canShowFCPAssetDialog"
        @click="showFCPAssetDialog"
      >
        <template v-slot:prepend>
          <v-icon size="small">mdi-download-box-outline</v-icon>
        </template>
        <v-list-item-title>{{ $t('routes.node.FCPAdd') }}</v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="canShowFCPSubtitleDialog"
        @click="showFCPSubtitleDialog"
      >
        <template v-slot:prepend>
          <v-icon size="small">mdi-download-box-outline</v-icon>
        </template>
        <v-list-item-title>{{ $t('routes.node.FCPAdd') }}</v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="canShowAdobePremiereDialog"
        @click="showAdobePremiereAssetDialog"
      >
        <template v-slot:prepend>
          <v-icon size="small">mdi-download-box-outline</v-icon>
        </template>
        <v-list-item-title>{{ $t('routes.node.adobePremiereAdd') }}</v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="canShowRenameDialog"
        @click="showRenameAssetDialog"
        data-cy="podFiles-tree-actions-renameAsset"
      >
        <template v-slot:prepend>
          <v-icon size="small">mdi-form-textbox</v-icon>
        </template>
        <v-list-item-title>{{ $t('routes.pod.renameAsset') }}</v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="canShowDeleteDialog"
        @click="showDeletePodDialog"
        data-cy="podFiles-tree-actions-deleteAsset"
      >
        <template v-slot:prepend>
          <v-icon size="small">mdi-trash-can-outline</v-icon>
        </template>
        <v-list-item-title>{{ $t('routes.pod.deleteAsset') }}</v-list-item-title>
      </v-list-item>

      <!-- Divider -->
      <v-divider v-if="isAssetCompleted" aria-hidden="true" />

      <v-list-item
        v-if="canShowAssetLanguageDialog"
        @click="showAssetLanguageDialog"
        data-cy="podFiles-tree-actions-setAssetLanguage"
      >
        <template v-slot:prepend>
          <v-icon size="small">mdi-web</v-icon>
        </template>
        <v-list-item-title>{{ $t('routes.pod.setAssetLanguage') }}</v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="canShowConvertToSrtDialog"
        @click="showConvertSrtSubtitleDialog"
        data-cy="podFiles-tree-actions-convertToSrt"
      >
        <template v-slot:prepend>
          <v-icon size="small">mdi-file-restore-outline</v-icon>
        </template>
        <v-list-item-title>{{ $t('routes.pod.convertSubtitleToSRT') }}</v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="canShowConvertToVttDialog"
        @click="showConvertVttSubtitleDialog"
        data-cy="podFiles-tree-actions-convertToVtt"
      >
        <template v-slot:prepend>
          <v-icon size="small">mdi-file-restore-outline</v-icon>
        </template>
        <v-list-item-title>{{ $t('routes.pod.convertSubtitleToVTT') }}</v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="canShowExportDialog"
        @click="showExportAssetDialog"
        data-cy="podFiles-tree-actions-exportAsset"
      >
        <template v-slot:prepend>
          <v-icon size="small">mdi-export</v-icon>
        </template>
        <v-list-item-title>{{ $t('routes.pod.exportAsset') }}</v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>

  <FinalCutProAssetDialog
    v-if="canShowFCPAssetDialog"
    v-model="showingFCPAssetDialog"
    :assets="[asset]"
  />

  <FCPSubtitleDialog
    v-if="canShowFCPSubtitleDialog"
    v-model="showingFCPSubtitleDialog"
    :assets="[asset]"
  />

  <AdobePremiereAssetDialog
    v-if="canShowAdobePremiereDialog"
    v-model="showingAdobePremiereAssetDialog"
    :assets="[asset]"
  />

  <ViewAssetDialog
    v-if="canShowViewAsset"
    v-model="showingViewAssetDialog"
    :pod="pod"
    :asset="asset"
  />

  <RenameAssetDialog
    v-if="canShowRenameDialog"
    v-model="showingRenameAssetDialog"
    :pod="pod"
    :asset="asset"
  />

  <DeletePodDialog
    v-if="canShowDeleteDialog"
    v-model="showingDeletePodDialog"
    :pod="pod"
    :asset="asset"
  />

  <AssetLanguageDialog
    v-if="canShowAssetLanguageDialog"
    v-model="showingAssetLanguageDialog"
    :pod="pod"
    :asset="asset"
  />

  <ConvertSubtitleDialog
    v-if="canShowConvertToVttDialog"
    v-model="showingConvertVttSubtitleDialog"
    :asset="asset"
  />

  <ConvertSubtitleDialog
    v-if="canShowConvertToSrtDialog"
    v-model="showingConvertSrtSubtitleDialog"
    :asset="asset"
  />

  <ExportAssetDialog
    v-if="canShowExportDialog"
    v-model="showingExportAssetDialog"
    :pod="pod"
    :asset="asset"
  />
</template>

<script>
import { useCommonStore } from '@/stores/CommonStore.js'
import MimetypeHelper from '@/components/MimetypeHelper.js'
import Api from '@/api/Api';

import AdobePremiereAssetDialog from './dialogs/AdobePremiereAssetDialog.vue'
import FinalCutProAssetDialog from './dialogs/FinalCutProAssetDialog.vue'
import FCPSubtitleDialog from './dialogs/FCPSubtitleDialog.vue'
import RenameAssetDialog from './dialogs/RenameAssetDialog.vue'
import DeletePodDialog from '@/routes/nodes2/components/dialogs/DeletePodDialog.vue'
import ViewAssetDialog from '@/routes/pods2/components/dialogs/ViewAssetDialog.vue';

import AssetLanguageDialog from './dialogs/AssetLanguageDialog.vue'
import ConvertSubtitleDialog from './dialogs/ConvertSubtitleDialog.vue'
import ExportAssetDialog from './dialogs/ExportAssetDialog.vue'

export default {
  emits: ["update:modelValue"],
  name: "AssetsContextMenu",
  components: {
    AdobePremiereAssetDialog,
    FinalCutProAssetDialog,
    FCPSubtitleDialog,
    RenameAssetDialog,
    DeletePodDialog,
    AssetLanguageDialog,
    ConvertSubtitleDialog,
    ExportAssetDialog,
    ViewAssetDialog,
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    pod: {
      type: Object,
      required: true,
    },
    asset: {
      type: Object,
    },
    positionX: {
      type: Number,
      required: true,
    },
    positionY: {
      type: Number,
      required: true,
    },
  },
  data: function() {
    return {
      showingFCPAssetDialog: false,
      showingFCPSubtitleDialog: false,
      showingAdobePremiereAssetDialog: false,
      showingRenameAssetDialog: false,
      showingDeletePodDialog: false,
      showingAssetLanguageDialog: false,
      showingConvertVttSubtitleDialog: false,
      showingConvertSrtSubtitleDialog: false,
      showingExportAssetDialog: false,
      showingViewAssetDialog: false,
    };
  },
  methods: {
    async downloadFile(asset) {
      if (this.pod.archived) return;
      this.showingMenu = false;

      if (asset.type === this.assetTypes.RAW) {
        try {
          const response = await Api.pod.issueRawAssetDownloadUrl(this.pod.id, asset.id);

          if (response?.data?.url) {
            window.location = response.data.url;
          }
        } catch (error) {
          const message = error?.response?.error
            ? this.$localizeMessage(error.response.error, this.$t('backend.system-error'))
            : this.$t('backend.system-error');
          this.$showMessage(message, { type: 'error' });
        }
        return;
      }

      window.location = asset.url;
    },
    stub: function () {
      this.showingMenu = false;
    },
    showFCPAssetDialog: function () {
      this.showingFCPAssetDialog = true;
      this.showingMenu = false;
    },
    showFCPSubtitleDialog: function () {
      this.showingFCPSubtitleDialog = true;
      this.showingMenu = false;
    },
    showAdobePremiereAssetDialog: function () {
      this.showingAdobePremiereAssetDialog = true;
      this.showingMenu = false;
    },
    showViewAssetDialog() {
      this.showingViewAssetDialog = true;
      this.showingMenu = false;
    },
    showRenameAssetDialog: function () {
      this.showingRenameAssetDialog = true;
      this.showingMenu = false;
    },
    showDeletePodDialog: function () {
      this.showingDeletePodDialog = true;
      this.showingMenu = false;
    },
    showAssetLanguageDialog: function () {
      this.showingAssetLanguageDialog = true;
      this.showingMenu = false;
    },
    showConvertVttSubtitleDialog: function () {
      this.showingConvertVttSubtitleDialog = true;
      this.showingMenu = false;
    },
    showConvertSrtSubtitleDialog: function () {
      this.showingConvertSrtSubtitleDialog = true;
      this.showingMenu = false;
    },
    showExportAssetDialog: function () {
      this.showingExportAssetDialog = true;
      this.showingMenu = false;
    },
  },
  computed: {
    showingMenu: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    hasAnyItemsInMenu: function() {
      return this.$canControlNodeAndPod() ||
              this.$canDownloadNodeAndPod();
    },
    assetStates: function() {
      const commonStore = useCommonStore();
      return commonStore.assetStates;
    },
    assetTypes: function() {
      const commonStore = useCommonStore();
      return commonStore.assetTypes;
    },
    isSRT: function() {
      const commonStore = useCommonStore();
      const states = commonStore.podStates;
      return !this.asset.broken && this.asset.state === states.COMPLETE && this.asset.type === this.assetTypes.SRT;
    },
    isVTT: function() {
      const commonStore = useCommonStore();
      const states = commonStore.podStates;
      return !this.asset.broken && this.asset.state === states.COMPLETE && this.asset.type === this.assetTypes.VTT;
    },
    isSupportedByFCPAsset: function() {
      return MimetypeHelper.isSupportedByFCPAsset(this.asset);
    },
    isSubtitleAsset: function() {
      return [this.assetTypes.VTT, this.assetTypes.SRT].indexOf(this.asset.type) >= 0;
    },
    allowExportToPod: function() {
      return true; // In 11824 we remove the restriction for exporting assests to pods

      // const allowedTypes = [
      //     this.assetTypes.THUMBNAIL, this.assetTypes.LIVEPREVIEW,
      //     this.assetTypes.FRAME, this.assetTypes.RAW,
      //     this.assetTypes.AUDIO, this.assetTypes.WEB, this.assetTypes.EXTERNAL,
      //     this.assetTypes.IMAGE, this.assetTypes.VIDEO,
      // ];

      // return allowedTypes.indexOf(this.item.type) >= 0;
    },
    isAssetCompleted: function() {
      return this.asset.state === this.assetStates.COMPLETE;
    },
    canShowDownloadAssetItem: function() {
      return !this.$isAdobeExtension() && !this.$isFCPExtension() && this.$canDownloadNodeAndPod() && this.isAssetCompleted;
    },
    canShowFCPAssetDialog: function() {
      return this.$isFCPExtension() && this.isSupportedByFCPAsset;
    },
    canShowFCPSubtitleDialog: function() {
      return this.$isFCPExtension() && this.isSubtitleAsset;
    },
    canShowAdobePremiereDialog: function() {
      return this.$isAdobeExtension();
    },
    canShowViewAsset: function() {
      return MimetypeHelper.isPdfAsset(this.asset) || MimetypeHelper.isDocxAsset(this.asset);
    },
    canShowRenameDialog: function() {
      return this.$canControlNodeAndPod() && this.isAssetCompleted;
    },
    canShowDeleteDialog: function() {
      return this.$canDeleteNodeAndPod();
    },
    canShowAssetLanguageDialog: function() {
      return this.$canControlNodeAndPod() && this.isAssetCompleted;
    },
    canShowConvertToSrtDialog: function() {
      return this.$canControlNodeAndPod() && this.isVTT;
    },
    canShowConvertToVttDialog: function() {
      return this.$canControlNodeAndPod() && this.isSRT;
    },
    canShowExportDialog: function() {
      return this.$canControlNodeAndPod() && this.allowExportToPod && this.isAssetCompleted;
    },
  }
};
</script>

<style>
</style>
